<template>
  <div
    class="green-building-certificate__table-group"
    :class="{'isEdit': isEdit}"
  >
    <table
      v-if="dataSource.length > 0"
      class="green-building-certificate__table"
    >
      <thead>
        <tr>
          <th class="index">
            #
          </th>
          <th
            v-for="column in columns"
            :key="column.dataIndex"
            :class="{ 'required-flag': isEdit, [column.dataIndex]: true }"
          >{{ column.title }}</th>
          <th v-if="isEdit" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) in dataSource"
          :key="index"
        >
          <td class="index">
            {{ index + 1 }}
          </td>
          <td
            v-for="column in columns"
            :key="column.dataIndex"
            :class="column.dataIndex"
          >
            <component
              :is="column.component.name"
              v-if="isEdit && column.component"
              v-bind="column.component.attrs || {}"
              v-model="item[column.dataIndex]"
              v-on="column.component.listeners || {}"
            />
            <template v-else>
              <template v-if="column.render">
                {{ column.render(item[column.dataIndex], item, index) }}
              </template>
              <template v-else>
                {{ item[column.dataIndex] }}
              </template>
            </template>
          </td>
          <td v-if="isEdit">
            <HKLDeleteIcon
              @click="() => deleteRow(index)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <span
      v-if="isEdit && docDataSource.length > 0"
      class="underline pointer"
      @click="() => addRow()"
    >
      {{ $t('tenancy.greenOperation.assessments.greenBuildingCertificate.addGreenBuilding') }} +
    </span>
  </div>
</template>

<script>
import HKLDateRange from '@/controls/Date/HKLDateRange.vue'
import FusionCheck from '@/controls/FusionCheckRender.vue'
import HKLDeleteIcon from '@/controls/Icon/HKLDeleteIcon.vue'
import HKLInput from '@/controls/Input/HKLInput.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import lodash from 'lodash'
import { computed, unref } from 'vue-demi'
import { useAssessmentDocumentTableAttrs, useUpdateGreenCertRecordDocs } from '../assessments/hook'
import { GREEN_OPERATION_ASSESSMENT_TYPE } from '../consts'
import { useGreenOperationAssessmentFormStatus, useGreenOperationField } from '../hook'
import { whenever } from '@vueuse/core'

export default {
  components: {
    HKLDeleteIcon,
  },
  props: {
    dataSource: {
      type: Array,
      default: () => ([]),
    },
  },
  setup (props) {
    const sourceId = useGreenOperationField('id', '')
    const { isEdit } = useGreenOperationAssessmentFormStatus()
    const tableAttrs = useAssessmentDocumentTableAttrs(sourceId, GREEN_OPERATION_ASSESSMENT_TYPE.GREEN_CERT)
    const docDataSource = computed(() => unref(tableAttrs).dataSource)
    const { init, updateUsingGreenCertRecordDocs } = useUpdateGreenCertRecordDocs()
    whenever(isEdit, () => {
      const rawDocs = (props.dataSource || []).reduce((rs, item) => {
        rs.push(...(item.documents || []))
        return rs
      }, [])
      init(rawDocs)
    })
    const columns = computed(() => [
      {
        title: i18n.t('table.header.issuer'),
        dataIndex: 'issuer',
        component: {
          name: FusionCheck,
          attrs: {
            component: HKLInput,
            fusioninputcheck: {
              type: 'string',
              showTip: false,
              required: true,
            },
          },
        },
      },
      {
        title: i18n.t('table.header.rating'),
        dataIndex: 'rating',
        component: {
          name: FusionCheck,
          attrs: {
            component: HKLInput,
            fusioninputcheck: {
              type: 'string',
              showTip: false,
              required: true,
            },
          },
        },
      },
      {
        title: i18n.t('table.header.effectivePeriod'),
        dataIndex: 'period',
        component: {
          name: HKLDateRange,
          attrs: {
            required: true,
            canPutTotop: false,
            placeholders: [i18n.t('dateFrom'), i18n.t('dateTo')],
            displayRange: [{ to: undefined }, { from: undefined, to: undefined }],
          },
        },
        render: (values = []) => {
          return values.map((value) => dateUtil.formatByLongDate(value)).join('-')
        },
      },
      {
        title: i18n.t('table.header.document'),
        dataIndex: 'documents',
        component: {
          name: FusionCheck,
          attrs: {
            component: HKLMultiSelect,
            fusioninputcheck: {
              showTip: false,
              required: true,
              patternOnly: (values = []) => {
                return values.length > 0
              },
            },
            options: lodash.orderBy(unref(docDataSource), ['submittedDateMs'], ['desc']).map((doc) => ({
              name: doc.filename,
              value: doc.id,
            })),
          },
          listeners: {
            select: (value) => {
              updateUsingGreenCertRecordDocs([value])
            },
            deselect: (value) => {
              updateUsingGreenCertRecordDocs([], [value])
            },
          },
        },
        render: (values) => {
          const usingDocs = unref(docDataSource).filter(doc => (values || []).includes(doc.id))
          return usingDocs.map(doc => doc.filename).join(', ')
        },
      },
    ])
    return {
      docDataSource,
      columns,
      isEdit,
      addRow: () => {
        const newObj = {
          period: [],
          documents: [],
          createdAt: new Date(),
        }
        props.dataSource.push(lodash.cloneDeep(newObj))
      },
      deleteRow: (index) => {
        const deletedItem = props.dataSource.splice(index, 1)
        const deletedDocs = lodash.get(deletedItem, '0.documents') || []
        deletedDocs.length && updateUsingGreenCertRecordDocs([], deletedDocs)
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.green-building-certificate {
  &__table {
    &, thead, tbody, tr {
      border-width: 0;
    }
    tr {
      background-color: initial;
    }
    .isEdit & .period {
      min-width: 270px;
    }
    .period ::v-deep .vdp-datepicker:has(input.fusion-input-check-is-invalid-input) {
      // if the first datepicker is invalid status but its left border width is 0
      & + .vdp-datepicker {
      }
    }
  }
}
</style>
